import React from 'react';
import { useTranslation } from 'react-i18next';

import { LinkStyle } from '@sola/ark-core/atoms/link-style/link-style.tsx';
import {
  ItemCard,
  ItemCardDivider,
  ItemCardFooter,
} from '@sola/ark-core/molecules/item-card/item-card.tsx';
import { cn } from '@sola/ark-core/utils/helpers.ts';
import { ArkIcon } from '@sola/ark-icons/lib/ark-icon.tsx';
import '@sola/locales/formatters.ts';
import { GraphQLCommitment } from '@sola/schema/dotcom/supporters/schema.generated.ts';

import { CommitmentDetailsSheet } from '../../organisms/commitments-details-sheet/commitment-details-sheet.tsx';
import { CommitmentCardInfoSection } from '../commitment-card-info-section/commitment-card-info-section.tsx';

export type CommitmentCardProps = {
  commitment: GraphQLCommitment;
  isActive?: boolean;
  onActiveChange?: (open: boolean, commitmentId: string) => void;
};

const CommitmentCardPaymentDate: React.FC<GraphQLCommitment> = ({
  isRecurring,
  paymentDateNext,
  paymentDatePending,
}) => {
  const { t } = useTranslation('my');

  if (!isRecurring) {
    return (
      <>
        <ArkIcon
          iconName="alert-triangle"
          className="mr-snug-lg"
          size="1.25rem"
        />
        <span>{t('my:commitments.notRecurring.title')}</span>
      </>
    );
  }

  if (paymentDateNext) {
    return t('my:commitments.nextPayment', {
      date: paymentDateNext,
    });
  }

  if (paymentDatePending) {
    return t('my:commitments.nextPaymentPending');
  }

  return null;
};

/**
 * Commitment card, shown for both commitments and giving history items.
 */
export const CommitmentCard: React.FC<CommitmentCardProps> = (props) => {
  const { t } = useTranslation(['my']);
  const { commitment, isActive, onActiveChange } = props;

  return (
    <ItemCard>
      <CommitmentCardInfoSection commitment={commitment} />
      {/* Footer */}
      <ItemCardDivider />
      <ItemCardFooter justify="between">
        <span
          className={cn(
            'flex items-center label-moderate label-neutral-mild label-sm md:label-md',
            {
              'label-warning': !commitment.isRecurring,
            },
          )}
        >
          <CommitmentCardPaymentDate {...commitment} />
        </span>

        <CommitmentDetailsSheet
          commitment={commitment!}
          isOpen={isActive}
          onOpenChange={onActiveChange}
          openTrigger={
            <div className="ml-auto">
              <LinkStyle
                noUnderline
                noPadding
                inline
                variant="primary"
                size="lg"
              >
                <button type="button" className="CommitmentCard__trigger">
                  {t('my:commitments.detailsSheet.triggerLabel')}
                </button>
              </LinkStyle>
            </div>
          }
        />
      </ItemCardFooter>
    </ItemCard>
  );
};
